import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { TSafetyInfoMgmt } from '../../../type';

interface MedicationContentModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (data: TSafetyInfoMgmt) => void;
    initialData?: TSafetyInfoMgmt;
    medicationName?: string;
}

const SafetyListTextArea = ({ value, onChange, showRemoveButton, onRemove }: { value?:string, onChange?: (value: string) => void, showRemoveButton?:boolean, onRemove?: () => void }) => {
    return (
        <div className={s.considerationItem}>
            <Input.TextArea
                placeholder="Enter safety consideration"
                maxLength={500}
                rows={3}
                showCount
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
            />
            {showRemoveButton && (
                <MinusCircleOutlined
                    className={s.removeButton}
                    onClick={onRemove}
                />
            )}
        </div>
    );
};

const SafetyInfoContentModal: React.FC<MedicationContentModalProps> = ({
    visible,
    onCancel,
    onSave,
    initialData,
    medicationName = 'Medication Content',
}) => {
    const [form] = Form.useForm<TSafetyInfoMgmt>();
    const [charCounts, setCharCounts] = useState({
        criticalSafetyInformation: 0,
    });

    // 更新字符计数
    const updateCharCounts = (data: Partial<TSafetyInfoMgmt>) => {
        setCharCounts({
            criticalSafetyInformation: (data.criticalSafetyInformation || '').length,
        });
    };

    // 当模态框打开或初始数据变化时，设置表单数据
    useEffect(() => {
        if (visible) {
            if (initialData) {
                updateCharCounts(initialData);
            } else {
                setCharCounts({
                    criticalSafetyInformation: 0,
                });
            }
        }
    }, [visible, initialData, form]);

    // 手动设置Form.List的值
    useEffect(() => {
        if (visible && initialData?.generalSafetyConsiderations) {
            // 延迟设置，确保Form.List已经渲染
            setTimeout(() => {
                form.setFieldsValue({
                    generalSafetyConsiderations: initialData.generalSafetyConsiderations,
                });
            }, 100);
        }
    }, [visible, initialData, form]);

    // 处理表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            // 过滤掉空的考虑事项
            const filteredConsiderations = values.generalSafetyConsiderations.filter(
                (item: string) => item && item.trim() !== '',
            );
            onSave({
                ...values,
                generalSafetyConsiderations: filteredConsiderations,
            });
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 处理字段值变化
    const handleFieldChange = (fieldName: keyof TSafetyInfoMgmt, value: string) => {
        form.setFieldValue(fieldName, value);

        // 更新字符计数
        const newCounts = { ...charCounts };
        if (fieldName === 'criticalSafetyInformation') {
            newCounts.criticalSafetyInformation = value.length;
        }
        setCharCounts(newCounts);
    };

    return (
        <Modal
            title={initialData ? `Edit ${medicationName} Content` : `Add ${medicationName} Content`}
            className={commonS.modalFixHeightWrap}
            open={visible}
            onCancel={handleCancel}
            okText="Save changes"
            onOk={handleSubmit}
            width={543}
            destroyOnClose
        >
            <Form
                key={initialData ? 'edit' : 'add'} // 强制重新渲染
                form={form}
                layout="vertical"
                className={s.form}
                initialValues={{
                    criticalSafetyInformation: initialData?.criticalSafetyInformation || '',
                    generalSafetyConsiderations: initialData?.generalSafetyConsiderations || [''],
                }}
            >
                {/* Critical Safety Information Field */}
                <Form.Item
                    label="Critical safety information"
                    name="criticalSafetyInformation"
                    rules={[
                        { required: true, message: 'Please enter critical safety information' },
                        { max: 3000, message: 'Critical safety information cannot exceed 3000 characters' },
                    ]}
                >
                    <Input.TextArea
                        placeholder="Enter critical safety information"
                        maxLength={3000}
                        onChange={(e) => handleFieldChange('criticalSafetyInformation', e.target.value)}
                    />
                </Form.Item>

                {/* General Safety Considerations Field */}
                <Form.List
                    name="generalSafetyConsiderations"
                    rules={[
                        {
                            validator: async (_, considerations) => {
                                if (!considerations || considerations.length === 0) {
                                    return Promise.reject(new Error('Please add at least one safety consideration'));
                                }
                                const validConsiderations = considerations.filter(
                                    (item: string) => item && item.trim() !== '',
                                );
                                if (validConsiderations.length === 0) {
                                    return Promise.reject(new Error('Please add at least one safety consideration'));
                                }
                            },
                        },
                    ]}
                >
                    {(fields, { add, remove }, { errors }) => {
                        return (
                            <>
                                <div className={s.formListHeader}>
                                    <span className={s.formListLabel}>General safety considerations</span>
                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        icon={<PlusOutlined />}
                                        className={s.addButton}
                                    >
                                        Add consideration
                                    </Button>
                                </div>
                                {fields.map(({ key, name, ...restField }) => {
                                    return (
                                        <Form.Item
                                            {...restField}
                                            name={[name]}
                                            key={key}
                                            rules={[
                                                { required: true, message: 'Please enter safety consideration' },
                                                { max: 500, message: 'Safety consideration cannot exceed 500 characters' },
                                            ]}
                                        >
                                            <SafetyListTextArea
                                                showRemoveButton={fields.length > 1}
                                                onRemove={() => remove(name)}
                                            />
                                            {/* <div className={s.considerationItem}>
                                                <Input.TextArea
                                                    placeholder="Enter safety consideration"
                                                    maxLength={500}
                                                    rows={3}
                                                    showCount
                                                />
                                                {fields.length > 1 && (
                                                    <MinusCircleOutlined
                                                        className={s.removeButton}
                                                        onClick={() => remove(name)}
                                                    />
                                                )}
                                            </div> */}
                                        </Form.Item>
                                    );
                                })}
                                {errors && <div className={s.formListError}>{errors}</div>}
                            </>
                        );
                    }}
                </Form.List>
            </Form>
        </Modal>
    );
};

export default SafetyInfoContentModal;
